import { useCallback, useEffect, useRef, useState, type DragEvent, type KeyboardEvent } from 'react';
import { useAccount } from '../auth/useAccount';
import { useToken } from '../auth/useToken';
import { Icon } from './Icon';
import {
  addPrompt,
  deletePrompt,
  listPrompts,
  reorderPrompts,
  type SavedPrompt,
} from '../lib/prompts';

/**
 * Iter-2 empty-state grid. The Sidebar no longer carries saved prompts —
 * this grid is the sole surface for them. So in addition to rendering and
 * adding, this is where drag-to-reorder + keyboard-reorder live.
 *
 * Each card is a `draggable` button. Clicking auto-sends the prompt; the
 * trash icon (hover-revealed) deletes; the grip-dots are a visual cue that
 * the card is movable. A mid-drag card gets `.is-lifted` (translate + tilt +
 * primary ring) and the card under the cursor gets `.is-drop-target` (dashed
 * primary outline + 30% content). The reorder fires `PUT /prompts/order`
 * optimistically and refetches on failure to resync.
 *
 * The legacy `onSubmit` prop path is kept so callers from the pre-iter-1
 * rewrite (if any survive) still work. Iter-1+iter-2 always supply `onPick`.
 */
export interface EmptyStateProps {
  prompts?: SavedPrompt[];
  onPromptsChange?: (next: SavedPrompt[]) => void;
  /** Iter-1+ path: prefill / auto-send the composer. */
  onPick?: (text: string) => void;
  /** Pre-iter-1 alias for `onPick`. */
  onSubmit?: (text: string) => void;
}

const REORDER_HINT_KEY = 'kairos.reorder.hintSeen';

export function EmptyState({ prompts: propPrompts, onPromptsChange: propOnChange, onPick, onSubmit }: EmptyStateProps) {
  const account = useAccount();
  const firstName = account?.name?.split(' ')[0] ?? 'there';
  const getToken = useToken();

  // Local fallback state when the parent hasn't lifted prompts yet —
  // iter-1+ ChatScreen always supplies propPrompts so this branch is dead.
  const [localPrompts, setLocalPrompts] = useState<SavedPrompt[]>([]);
  const prompts = propPrompts ?? localPrompts;
  const setPrompts = propOnChange ?? setLocalPrompts;

  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');

  // Drag-to-reorder state — null while idle, set to the prompt id of the
  // picked / hovered card during a drag.
  const [draggedId, setDraggedId] = useState<number | null>(null);
  const [dropTargetId, setDropTargetId] = useState<number | null>(null);

  // First-time hint banner. Visible until the user actually triggers a
  // reorder (drag drop OR keyboard swap); persisted to localStorage so we
  // don't nag returning users on every visit.
  const [showReorderHint, setShowReorderHint] = useState(() => {
    if (typeof window === 'undefined') return false;
    try { return localStorage.getItem(REORDER_HINT_KEY) !== '1'; }
    catch { return false; }
  });

  // Touch-device detection. On iOS Safari, draggable={true} on a button
  // makes click events "almost never fire" — Safari reserves the first
  // touch for a possible drag initiation (a known bug, see
  // https://github.com/atlassian/react-beautiful-dnd/issues/1367). Drop
  // draggable on touch devices entirely; users reorder via long-press
  // keyboard navigation (⌥+↑/↓) or just delete + recreate.
  const [isTouchOnly, setIsTouchOnly] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(hover: none) and (pointer: coarse)');
    const update = () => setIsTouchOnly(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  const dismissReorderHint = useCallback(() => {
    setShowReorderHint(false);
    try { localStorage.setItem(REORDER_HINT_KEY, '1'); } catch { /* no-op */ }
  }, []);

  // Self-fetch ONLY when no parent has supplied the list (pre-iter-1
  // callers). Iter-1+ ChatScreen sets propPrompts so this is dead.
  useEffect(() => {
    if (propPrompts !== undefined) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await listPrompts(await getToken());
        if (!cancelled) setLocalPrompts(list);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [getToken, propPrompts]);

  const onCardClick = (text: string) => {
    if (onPick) onPick(text);
    else if (onSubmit) onSubmit(text);
  };

  const onDelete = async (id: number) => {
    try {
      await deletePrompt(await getToken(), id);
      setPrompts(prompts.filter(p => p.id !== id));
    } catch { /* silent — next refresh resolves */ }
  };

  const onSaveDraft = async () => {
    const text = draft.trim();
    if (!text) { setAdding(false); setDraft(''); return; }
    try {
      const created = await addPrompt(await getToken(), text);
      setPrompts([...prompts, created]);
    } catch { /* swallow */ }
    setAdding(false);
    setDraft('');
  };

  // ---- Drag-to-reorder handlers ------------------------------------------
  // The drag image is replaced with a 1×1 transparent gif so the browser's
  // default ghost doesn't compete with the CSS-styled .is-lifted card.
  const transparentImgRef = useRef<HTMLImageElement | null>(null);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const img = new Image();
    img.src = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';
    transparentImgRef.current = img;
  }, []);

  const onDragStart = (e: DragEvent, id: number) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
    const ghost = transparentImgRef.current;
    if (ghost) e.dataTransfer.setDragImage(ghost, 0, 0);
  };
  const onDragOver = (e: DragEvent, id: number) => {
    e.preventDefault();                 // required to allow drop
    e.dataTransfer.dropEffect = 'move';
    if (dropTargetId !== id) setDropTargetId(id);
  };
  const onDragLeave = (id: number) => {
    setDropTargetId(prev => (prev === id ? null : prev));
  };
  const onDrop = async (e: DragEvent, targetId: number) => {
    e.preventDefault();
    const src = draggedId;
    setDraggedId(null);
    setDropTargetId(null);
    if (src == null || src === targetId) return;
    const fromIdx = prompts.findIndex(p => p.id === src);
    const toIdx = prompts.findIndex(p => p.id === targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = prompts.slice();
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    // Renumber sortOrder locally for an instant render; server normalises
    // on reorder so any drift is corrected on next fetch.
    const renumbered = next.map((p, i) => ({ ...p, sortOrder: i }));
    setPrompts(renumbered);
    dismissReorderHint();
    try {
      await reorderPrompts(await getToken(), renumbered.map(p => p.id));
    } catch (err) {
      console.error('reorderPrompts failed; refetching to resync', err);
      try { setPrompts(await listPrompts(await getToken())); } catch { /* no-op */ }
    }
  };
  const onDragEnd = () => {
    setDraggedId(null);
    setDropTargetId(null);
  };

  // ---- Keyboard reorder: ⌥/⌘ + ArrowUp/ArrowDown moves the focused card.
  //      Plain arrows still scroll, so we require a modifier.
  const onKeyDown = async (e: KeyboardEvent, id: number) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    if (!(e.altKey || e.metaKey)) return;
    e.preventDefault();
    const idx = prompts.findIndex(p => p.id === id);
    const newIdx = e.key === 'ArrowUp' ? idx - 1 : idx + 1;
    if (idx < 0 || newIdx < 0 || newIdx >= prompts.length) return;
    const next = prompts.slice();
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    const renumbered = next.map((p, i) => ({ ...p, sortOrder: i }));
    setPrompts(renumbered);
    dismissReorderHint();
    try {
      await reorderPrompts(await getToken(), renumbered.map(p => p.id));
    } catch (err) {
      console.error('reorderPrompts failed; refetching to resync', err);
      try { setPrompts(await listPrompts(await getToken())); } catch { /* no-op */ }
    }
    // Restore focus to the moved card so subsequent keystrokes keep moving
    // the same row.
    requestAnimationFrame(() => {
      document.querySelector<HTMLButtonElement>(`[data-prompt-id="${id}"]`)?.focus();
    });
  };

  return (
    <div className="empty-state">
      <img src="/kairos-icon.png" alt="" width={56} height={56} className="rounded-lg mb-4" />
      <div className="empty-title">How can I help, {firstName}?</div>
      <div className="empty-subtitle">Pick a saved prompt, or ask anything about Trailhead.</div>

      <div className="empty-grid">
        {prompts.map(p => {
          const isLifted = draggedId === p.id;
          const isDropTarget = dropTargetId === p.id && draggedId !== p.id;
          const className = [
            'empty-prompt',
            isLifted && 'is-lifted',
            isDropTarget && 'is-drop-target',
          ].filter(Boolean).join(' ');
          return (
            <button
              key={p.id}
              type="button"
              data-prompt-id={p.id}
              draggable={!isTouchOnly}
              aria-grabbed={isLifted}
              onDragStart={isTouchOnly ? undefined : (e) => onDragStart(e, p.id)}
              onDragOver={isTouchOnly ? undefined : (e) => onDragOver(e, p.id)}
              onDragLeave={isTouchOnly ? undefined : () => onDragLeave(p.id)}
              onDrop={isTouchOnly ? undefined : (e) => onDrop(e, p.id)}
              onDragEnd={isTouchOnly ? undefined : onDragEnd}
              onKeyDown={(e) => { void onKeyDown(e, p.id); }}
              onClick={() => { if (!isLifted) onCardClick(p.text); }}
              className={className}
            >
              {/* Grip dots — hover-revealed visual handle. Decorative; the
                  whole card is the drag source. Matches Notion/Linear UX. */}
              <span className="empty-prompt__grip" aria-hidden="true">⋮⋮</span>
              <Icon name={p.icon} size={16} />
              <span className="empty-prompt__text">{p.text}</span>
              <span
                role="button"
                tabIndex={-1}
                className="empty-prompt__delete"
                onClick={(e) => { e.stopPropagation(); void onDelete(p.id); }}
                onDragStart={(e) => e.preventDefault()}   /* don't start a drag from the trash */
                aria-label={`Delete saved prompt "${p.text}"`}
                title="Delete"
              >
                <Icon name="i-trash" size={12} />
              </span>
            </button>
          );
        })}

        {adding ? (
          <div className="empty-edit-row" style={{ gridColumn: 'span 2' }}>
            <input
              type="text"
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); void onSaveDraft(); }
                if (e.key === 'Escape') { setAdding(false); setDraft(''); }
              }}
              placeholder="Type a prompt and press Enter"
              aria-label="New prompt text"
            />
            <button type="button" className="empty-edit-row__cancel" onClick={() => { setAdding(false); setDraft(''); }}>
              Cancel
            </button>
            <button type="button" className="empty-edit-row__save" onClick={onSaveDraft}>
              Save
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="empty-add-tile"
            onClick={() => { setAdding(true); setDraft(''); }}
          >
            + Add a prompt to your saved list
          </button>
        )}

        {showReorderHint && prompts.length >= 2 && (
          <div className="empty-grid__hint">
            Drag, or hold ⌥/⌘ and use ↑/↓ to reorder
          </div>
        )}
      </div>
    </div>
  );
}
